import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { useApi } from '../lib/api';
import { useMe } from '../lib/me';
import { dateTimeCentral, money, periodLabel, ratio } from '../lib/format';
import PeriodPicker, { usePeriod } from '../components/PeriodPicker';

type Kind = 'internal' | 'midcap_monthly' | 'midcap_quarterly';
const KIND_LABEL: Record<Kind, string> = {
  internal: 'Internal consolidated financials',
  midcap_monthly: 'MidCap monthly covenants',
  midcap_quarterly: 'MidCap quarterly covenant workbook',
};

interface Run { run_id: number; kind: Kind; period: string; filename: string; generated_by: string; generated_at: string }
interface CovenantTest { totalDebt: number; cash: number; cashNetted: number; netDebt: number; ebitda: number; ratio: number | null; level: number | null; pass: boolean | null }
interface GenResult {
  run_id: number;
  filename: string;
  warnings?: string[];
  covenant?: CovenantTest;
  templateMethod?: CovenantTest;
  ttm?: { cap: { cappedAddbacks: number; limit: number; limitation: number }; covenantEbitda: number } | null;
  tieOut?: { cell: string; label: string; period: string; previous: number; app: number; diff: number }[];
}
interface Definitions { covenantInputs: { key: string; label: string }[] }
interface Inputs { covenant: { input_key: string; amount: number }[] }

function CovenantCard({ title, t }: { title: string; t: CovenantTest }) {
  return (
    <div className="card p-4">
      <div className="font-semibold text-denim">{title}</div>
      <table className="w-full text-sm mt-2">
        <tbody>
          <tr><td>Total debt</td><td className="num">{money(t.totalDebt)}</td></tr>
          <tr><td>Cash netted (of {money(t.cash)})</td><td className="num">({money(t.cashNetted)})</td></tr>
          <tr><td>Net debt</td><td className="num">{money(t.netDebt)}</td></tr>
          <tr><td>TTM EBITDA</td><td className="num">{money(t.ebitda)}</td></tr>
          <tr className="font-semibold border-t border-allstar-gray">
            <td>Total net leverage vs {ratio(t.level, 1)}</td>
            <td className={`num ${t.pass === false ? 'text-negative' : t.pass ? 'text-positive' : ''}`}>
              {ratio(t.ratio)} {t.pass === null ? '' : t.pass ? '✓' : '✗'}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function Outputs() {
  const api = useApi();
  const qc = useQueryClient();
  const isAdmin = useMe().data?.role === 'admin';
  const [period, setPeriod] = usePeriod();
  const isQ = [3, 6, 9, 12].includes(Number(period.slice(5)));
  const [cov, setCov] = useState<Record<string, string>>({});

  const runs = useQuery({ queryKey: ['runs'], queryFn: () => api.get<Run[]>('/api/outputs/runs') });
  const defs = useQuery({ queryKey: ['defs'], queryFn: () => api.get<Definitions>('/api/adjustments/definitions') });
  const inputs = useQuery({ queryKey: ['inputs', period], queryFn: () => api.get<Inputs>(`/api/adjustments/inputs?period=${period}`), enabled: isQ });

  useEffect(() => {
    setCov(Object.fromEntries((inputs.data?.covenant ?? []).map((c) => [c.input_key, String(c.amount)])));
  }, [inputs.data]);

  const saveCov = useMutation({
    mutationFn: () => api.put('/api/adjustments/covenant', Object.entries(cov)
      .filter(([, v]) => v.trim() !== '' && Number.isFinite(Number(v)))
      .map(([input_key, v]) => ({ period, input_key, amount: Number(v) }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inputs', period] }),
  });

  const gen = useMutation({
    mutationFn: (kind: Kind) => api.post<GenResult>('/api/outputs/generate', { kind, period }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['runs'] }),
  });
  const r = gen.data;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl text-denim">Outputs — {periodLabel(period)}</h1>
          <p className="text-sm text-allstar-gray-dark mt-1">Every file is saved with a hash of the numbers that produced it.</p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      {isQ && (
        <div className="card p-5">
          <h2 className="font-semibold text-denim mb-3">Quarter-end covenant inputs</h2>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {defs.data?.covenantInputs.map((c) => (
              <label key={c.key} className="flex items-center justify-between gap-3">
                <span>{c.label}</span>
                <input className="border border-allstar-gray rounded px-2 py-1 w-40 text-right tabular-nums" disabled={!isAdmin}
                  value={cov[c.key] ?? ''} onChange={(e) => setCov({ ...cov, [c.key]: e.target.value })} />
              </label>
            ))}
          </div>
          {isAdmin && <button className="btn-ghost mt-3" disabled={saveCov.isPending} onClick={() => saveCov.mutate()}>Save covenant inputs</button>}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {(['internal', 'midcap_monthly', ...(isQ ? ['midcap_quarterly'] : [])] as Kind[]).map((k) => (
          <button key={k} className={k === 'internal' ? 'btn-ghost' : 'btn-primary'} disabled={gen.isPending} onClick={() => gen.mutate(k)}>
            <FileSpreadsheet className="w-4 h-4" /> {KIND_LABEL[k]}
          </button>
        ))}
      </div>
      {gen.isPending && <div className="text-sm text-allstar-gray-dark">Pulling Sage, Hub and Rowcal data and building the workbook…</div>}
      {gen.error && <div className="card p-4 border-l-4 border-l-negative text-sm">{(gen.error as Error).message}</div>}

      {r && (
        <div className="space-y-4">
          <div className="card p-4 flex items-center justify-between">
            <div className="font-semibold">{r.filename}</div>
            <button className="btn-accent" onClick={() => api.download(`/api/outputs/runs/${r.run_id}/file`, r.filename)}>
              <Download className="w-4 h-4" /> Download
            </button>
          </div>
          {r.warnings?.map((w) => (
            <div key={w} className="card p-3 border-l-4 border-l-caution text-sm flex gap-2"><AlertTriangle className="w-4 h-4 text-caution shrink-0 mt-0.5" />{w}</div>
          ))}
          {r.covenant && r.templateMethod && (
            <div className="grid md:grid-cols-2 gap-4">
              <CovenantCard title="Credit agreement method ($10M cash cap, 30% addback cap)" t={r.covenant} />
              <CovenantCard title="What the template's formulas compute" t={r.templateMethod} />
            </div>
          )}
          {!!r.tieOut?.length && (
            <div className="card overflow-hidden">
              <div className="p-3 font-semibold text-denim">Differences vs previously submitted months ({r.tieOut.length})</div>
              <table className="table-default">
                <thead><tr><th>Month</th><th>Row</th><th>Cell</th><th className="num">Submitted</th><th className="num">App</th><th className="num">Diff</th></tr></thead>
                <tbody>
                  {r.tieOut.map((t) => (
                    <tr key={t.cell}><td>{t.period}</td><td>{t.label}</td><td className="font-mono">{t.cell}</td>
                      <td className="num">{money(t.previous)}</td><td className="num">{money(t.app)}</td><td className="num">{money(t.diff)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="table-default">
          <thead><tr><th>Generated (CT)</th><th>Period</th><th>Output</th><th>By</th><th></th></tr></thead>
          <tbody>
            {runs.data?.map((run) => (
              <tr key={run.run_id}>
                <td>{dateTimeCentral(run.generated_at)}</td>
                <td>{periodLabel(run.period)}</td>
                <td>{KIND_LABEL[run.kind]}</td>
                <td>{run.generated_by}</td>
                <td className="text-right">
                  <button className="text-denim underline text-sm" onClick={() => api.download(`/api/outputs/runs/${run.run_id}/file`, run.filename)}>Download</button>
                </td>
              </tr>
            ))}
            {runs.data?.length === 0 && <tr><td colSpan={5} className="text-center text-allstar-gray-dark py-6">Nothing generated yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
